<template>
  <div class="modal-backdrop">
    <div class="modal-container">
      <header class="modal-header">
        <h2>{{ heading }}</h2>
      </header>
      <section class="modal-body">
        <p>
          Welcome to Celestia! Choose a character portrait
          <template v-if="!nicknameLocked">
            and a unique, permanent nickname. This is how other players will
            see you in-game and it
            <strong>cannot be changed later</strong>.
          </template>
          <template v-else>
            to finish setting up your profile. Your avatar
            <strong>cannot be changed later</strong>.
          </template>
        </p>
        <form @submit.prevent="handleSubmit">
          <div class="setup-top">
            <div class="setup-fields">
              <div class="input-group">
                <label for="nickname">Nickname (min 3 characters)</label>
                <input
                  type="text"
                  id="nickname"
                  v-model="nickname"
                  required
                  minlength="3"
                  :disabled="nicknameLocked"
                />
              </div>

              <div class="avatar-filters">
                <div class="input-group">
                  <label for="gender-filter">Gender</label>
                  <select id="gender-filter" v-model="genderFilter">
                    <option value="">All</option>
                    <option
                      v-for="option in genderOptions"
                      :key="option"
                      :value="option"
                    >
                      {{ capitalize(option) }}
                    </option>
                  </select>
                </div>
                <div class="input-group">
                  <label for="hair-filter">Hair Color</label>
                  <select id="hair-filter" v-model="hairFilter">
                    <option value="">All</option>
                    <option
                      v-for="option in hairOptions"
                      :key="option"
                      :value="option"
                    >
                      {{ capitalize(option) }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="avatar-preview" aria-hidden="true">
              <AvatarPortrait
                v-if="previewAvatar"
                :avatar="previewAvatar"
                :size="128"
              />
              <div v-else class="avatar-preview-empty">?</div>
            </div>
          </div>

          <div class="avatar-grid" role="listbox" aria-label="Avatar selection">
            <button
              v-for="avatar in filteredAvatars"
              :key="avatar.id"
              type="button"
              class="avatar-option"
              :class="{ selected: selectedAvatarId === avatar.id }"
              role="option"
              :aria-selected="selectedAvatarId === avatar.id"
              @click="selectedAvatarId = avatar.id"
              @mouseenter="hoveredAvatarId = avatar.id"
              @mouseleave="hoveredAvatarId = null"
            >
              <AvatarPortrait :avatar="avatar" :size="72" />
            </button>
          </div>
          <p v-if="!filteredAvatars.length" class="empty-filter">
            No avatars match these filters.
          </p>

          <p v-if="serverError" class="error-message">
            {{ serverError }}
          </p>
          <button
            type="submit"
            class="submit-button"
            :disabled="isLoading || !canSubmit"
          >
            {{ isLoading ? "Confirming..." : confirmLabel }}
          </button>
        </form>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useAuthStore } from "../store/auth";
import avatars from "@/data/avatars.json";
import AvatarPortrait from "./AvatarPortrait.vue";

const authStore = useAuthStore();
const nicknameLocked = computed(() => Boolean(authStore.user?.nickname));
const nickname = ref(authStore.user?.nickname || "");
const selectedAvatarId = ref("");
const hoveredAvatarId = ref(null);
const genderFilter = ref("");
const hairFilter = ref("");
const serverError = ref("");
const isLoading = ref(false);

const genderOptions = ["female", "male"];
const hairOptions = computed(() => {
  const colors = new Set(avatars.map((a) => a.hair_color));
  return [...colors].sort();
});

const filteredAvatars = computed(() => {
  return avatars.filter((avatar) => {
    if (genderFilter.value && avatar.gender !== genderFilter.value) return false;
    if (hairFilter.value && avatar.hair_color !== hairFilter.value) return false;
    return true;
  });
});

const previewAvatar = computed(() => {
  const hovered = hoveredAvatarId.value
    ? avatars.find((a) => a.id === hoveredAvatarId.value)
    : null;
  if (hovered) return hovered;
  if (!selectedAvatarId.value) return null;
  return avatars.find((a) => a.id === selectedAvatarId.value) || null;
});

const heading = computed(() =>
  nicknameLocked.value ? "Choose Your Avatar" : "Set Your Nickname & Avatar"
);

const confirmLabel = computed(() =>
  nicknameLocked.value ? "Confirm Avatar" : "Confirm Nickname & Avatar"
);

const canSubmit = computed(() => {
  const nickOk = nicknameLocked.value || nickname.value.length >= 3;
  return nickOk && Boolean(selectedAvatarId.value);
});

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const handleSubmit = async () => {
  serverError.value = "";
  if (!nicknameLocked.value && nickname.value.length < 3) {
    serverError.value = "Nickname must be at least 3 characters long.";
    return;
  }
  if (!selectedAvatarId.value) {
    serverError.value = "Please select an avatar.";
    return;
  }

  isLoading.value = true;
  try {
    await authStore.setNickname(nickname.value, selectedAvatarId.value);
  } catch (error) {
    serverError.value =
      error.response?.data?.message || "An unknown error occurred.";
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10050;
}

.modal-container {
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  background-color: #0a0a14;
  border: 1px solid #00f6ff;
  border-radius: 10px;
  box-shadow: 0 0 30px rgba(0, 246, 255, 0.5);
  display: flex;
  flex-direction: column;
  margin: 1rem;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 246, 255, 0.3);
}
.modal-header h2 {
  margin: 0;
  text-align: center;
}

.modal-body {
  padding: 1.5rem;
  color: #c0c0c0;
  line-height: 1.6;
  overflow-y: auto;
}

.modal-body p {
  text-align: center;
  margin-bottom: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-bottom: 1rem;
}

.input-group label {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #00f6ff;
}

.input-group input,
.input-group select {
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid #00f6ff;
  color: white;
  padding: 10px;
  border-radius: 5px;
}

.input-group select option {
  background-color: #0a0a14;
  color: white;
}

.input-group input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.setup-top {
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.setup-fields {
  flex: 1;
  min-width: 0;
}

.setup-fields .avatar-filters .input-group {
  margin-bottom: 0;
}

.avatar-preview {
  flex-shrink: 0;
  width: 128px;
  height: 128px;
  margin-top: 1.5rem;
  overflow: hidden;
  border-radius: 8px;
}

.avatar-preview-empty {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 2px dashed rgba(0, 246, 255, 0.35);
  border-radius: 8px;
  color: rgba(192, 192, 192, 0.55);
  font-size: 2.5rem;
  font-weight: 300;
  line-height: 1;
}

.avatar-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.avatar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.6rem;
  max-height: 280px;
  overflow-y: auto;
  padding: 0.25rem;
  margin-bottom: 0.5rem;
}

.avatar-option {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: 2px solid rgba(0, 246, 255, 0.35);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.avatar-option:hover {
  border-color: #00f6ff;
}

.avatar-option.selected {
  border-color: #00f6ff;
  box-shadow: 0 0 12px rgba(0, 246, 255, 0.55);
}

.empty-filter {
  margin-top: 0;
  font-size: 0.9rem;
}

.submit-button {
  width: 100%;
  margin-top: 1rem;
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-message {
  color: #ff4d4d;
  font-weight: bold;
  text-align: center;
  margin-top: 1rem;
  margin-bottom: 0;
}
</style>
